import { useState, useRef, useEffect } from 'react'
import { useShallow } from 'zustand/react/shallow'
import { addRelation } from 'api/relations'
import { bindInvites, usersBatchInvite } from 'api/user'
import Sidebar from 'components/Sidebar'
import Button from 'components/UI/Button'
import Textarea from 'components/UI/Textarea'
import { t, wait } from 'helpers'
import { useAppStore, useCastingStore } from 'store'
import { CASTING_STATUS, RELATIONS } from 'types/enums'
import useContentFullRelation from 'hooks/api/useContentFullRelation'
import { notif } from 'helpers/notif'
import { EventBus } from 'helpers/EventBus'

const RejectNdaSidebar = (): JSX.Element => {
	const [comment, setComment] = useState(t('APP_REJECT_NDA_DEFAULT'))
	const [openSidebar, setOpenSidebar, reviewNda, setReviewNdaSidebar, roleId] =
		useCastingStore(
			useShallow((state) => [
				state.openRejectNdaSidebar,
				state.setOpenRejectNdaSidebar,
				state.openReviewNdaSidebar,
				state.setOpenReviewNdaSidebar,
				state.openReviewNdaSidebar?.contentId,
			]),
		)
	const reviewNdaRef = useRef<typeof reviewNda | null>(null)

	const { contents: playlist } = useContentFullRelation(
		roleId,
		RELATIONS.PLAYLIST,
		true,
	)
	const contFull = playlist?.[0]

	useEffect(() => {
		if (reviewNda) {
			reviewNdaRef.current = reviewNda
		}
	}, [reviewNda])

	const onClose = (): void => {
		reviewNda?.refresh()
		const { setDocumentViewerSidebar } = useAppStore.getState()
		setDocumentViewerSidebar(null)
		setReviewNdaSidebar(null)
		setOpenSidebar(false)
		reviewNdaRef.current = null
	}

	const onReject = async (): Promise<void> => {
		const localReviewNda = reviewNdaRef.current
		if (localReviewNda) {
			await addRelation({
				sourceId: localReviewNda?.contentId,
				targetId: localReviewNda?.userId || '',
				type: 'Content',
				relation: RELATIONS.ACCESS,
				status: CASTING_STATUS.REJECTED,
				query: `?emailTemplateId=GenericMessage&subject=${t(
					'APP_MESSAGE_NDA_REJECTED_SUBJECT',
				)}&message=${encodeURIComponent(
					t('APP_MESSAGE_NDA_REJECTED_BODY').replace(
						'[RoleName]',
						contFull?.['Globalizationen-USContentTitle'] || '',
					),
				)}`,
			})
			await usersBatchInvite({
				sourceId: localReviewNda?.contentId,
				customerIds: [localReviewNda?.userId],
				message: comment,
				subject: '[YourName] rejected for Casting',
				relation: RELATIONS.ACCESS,
				type: 'Content',
			})
			await bindInvites(localReviewNda?.contentId)
			await wait(1000)
			notif('success', t('APP_NDA_REJECTED'))
			EventBus.$emit('refreshCDboard')
			reviewNdaRef.current = null
		}
		onClose()
	}

	return (
		<Sidebar
			open={openSidebar}
			onClose={onClose}
			iconClose
			header={{
				title: 'APP_CASTING_REJECT_NDA_SIDEBAR_TITLE',
				description: 'APP_CASTING_REJECT_NDA_SIDEBAR_DESCRIPTION',
			}}
			style={{ height: '100%' }}>
			<>
				<Textarea
					name="reject_comment"
					label="APP_REJECT_COMMENT"
					defaultValue={t('APP_REJECT_NDA_DEFAULT')}
					onChange={(e) => setComment(e.target.value)}
					rows={15}
				/>

				<div className="grid2column" style={{ marginTop: 'auto' }}>
					<Button onClick={() => setOpenSidebar(false)}>
						{t('APP_CANCEL')}
					</Button>
					<Button variant="themed" onClick={onReject}>
						{t('APP_CONFIRM')}
					</Button>
				</div>
			</>
		</Sidebar>
	)
}

export default RejectNdaSidebar
